import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BarChart3, MousePointerClick, Eye, UserPlus, DollarSign } from "lucide-react";

export default function AdminAnalytics() {
  const { user, loaded } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    if (loaded && (!user || user === false)) nav("/login");
    else if (loaded && user && user.role !== "admin") nav("/");
  }, [user, loaded, nav]);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get(`/admin/analytics/overview?days=${days}`).then((r) => setData(r.data)).catch(() => {});
    }
  }, [user, days]);

  if (!user || user.role !== "admin") return null;
  if (!data) return <div className="p-12" style={{ color: "var(--text-secondary)" }}>Cargando…</div>;

  const totals = data.totals || {};
  const maxTimeline = Math.max(...(data.timeline || []).map((d) => d.views), 1);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="admin-analytics-page">
      <div className="overline mb-3">Analytics</div>
      <h1 className="font-serif text-5xl font-light mb-2" style={{ color: "var(--text-primary)" }}>
        Panel de <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>analíticas</em>
      </h1>
      <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>
        Visibilidad completa de clics, páginas vistas y eventos de conversión.
      </p>

      <div className="flex gap-2 mb-8" data-testid="days-selector">
        {[1, 7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className="px-4 py-2 text-xs uppercase tracking-[0.2em]" data-testid={`days-${d}`}
            style={{
              color: days === d ? "var(--brand-primary)" : "var(--text-secondary)",
              border: `1px solid ${days === d ? "var(--brand-primary)" : "var(--border-subtle)"}`,
              background: days === d ? "var(--bg-elevated)" : "transparent",
            }}>
            {d === 1 ? "Hoy" : `${d} días`}
          </button>
        ))}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        {[
          { label: "Clics", value: totals.clicks || 0, icon: <MousePointerClick size={16} /> },
          { label: "Páginas vistas", value: totals.pageviews || 0, icon: <Eye size={16} /> },
          { label: "Nuevos usuarios", value: totals.new_users || 0, icon: <UserPlus size={16} /> },
          { label: "Nuevos perfiles", value: totals.new_profiles || 0, icon: <BarChart3 size={16} /> },
          { label: "Pagos", value: totals.new_paid || 0, icon: <DollarSign size={16} /> },
        ].map((s) => (
          <div key={s.label} className="p-4" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }} data-testid={`total-${s.label}`}>
            <div className="flex items-center gap-2 overline mb-2">{s.icon}{s.label}</div>
            <div className="font-serif text-3xl" style={{ color: "var(--brand-primary)" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Timeline */}
      {data.timeline && data.timeline.length > 0 && (
        <div className="mb-10">
          <div className="overline mb-3">Línea de tiempo — páginas vistas</div>
          <div className="flex items-end gap-1 h-40 p-4" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
            {data.timeline.map((d) => (
              <div key={d.date} className="flex-1 relative group" style={{ minWidth: "6px" }}>
                <div style={{ height: `${(d.views / maxTimeline) * 100}%`, background: "var(--brand-primary)", minHeight: "2px" }} />
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 text-xs whitespace-nowrap px-2 py-1 z-10"
                  style={{ background: "var(--bg-elevated)", color: "var(--text-primary)" }}>
                  {d.date}: {d.views} vistas
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top buttons */}
        <div className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }} data-testid="top-buttons-card">
          <div className="overline mb-4">Top botones (por clics)</div>
          {(data.top_buttons || []).slice(0, 15).map((b) => (
            <div key={b.button_id} className="flex justify-between items-center py-2" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <code className="text-xs" style={{ color: "var(--text-primary)" }}>{b.button_id}</code>
              <span className="text-sm" style={{ color: "var(--brand-primary)" }}>{b.clicks} <span style={{ color: "var(--text-muted)" }}>({b.unique} únicos)</span></span>
            </div>
          ))}
          {(data.top_buttons || []).length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>Sin clics registrados.</p>}
        </div>

        {/* Top pages */}
        <div className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }} data-testid="top-pages-card">
          <div className="overline mb-4">Top páginas</div>
          {(data.top_pages || []).slice(0, 15).map((p) => (
            <div key={p.path} className="flex justify-between items-center py-2" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <code className="text-xs" style={{ color: "var(--text-primary)" }}>{p.path}</code>
              <span className="text-sm" style={{ color: "var(--brand-primary)" }}>{p.views} <span style={{ color: "var(--text-muted)" }}>({p.unique} únicos)</span></span>
            </div>
          ))}
          {(data.top_pages || []).length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>Sin páginas vistas.</p>}
        </div>

        {/* Events */}
        <div className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }} data-testid="events-card">
          <div className="overline mb-4">Eventos clave</div>
          {Object.entries(data.event_totals || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between py-2" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <span className="text-sm" style={{ color: "var(--text-primary)" }}>{k}</span>
              <span className="text-sm" style={{ color: "var(--vip-gold)" }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Role clicks */}
        <div className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }} data-testid="role-clicks-card">
          <div className="overline mb-4">Clics por rol</div>
          {Object.entries(data.role_clicks || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between py-2" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
              <span className="text-sm" style={{ color: "var(--text-primary)" }}>{k}</span>
              <span className="text-sm" style={{ color: "var(--brand-primary)" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
